import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Box,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Paper,
    LinearProgress,
    StepConnector,
    Chip,
    Tooltip,
} from '@mui/material';
import { Send, ArrowBack, ArrowForward, Info, Edit, TaskAlt, Warning, CheckCircle, Error, VideoCall, CloudUpload } from '@mui/icons-material';
import Styles from './UserCaseStudy.module.scss';
import axios from 'axios';

export const UserCaseStudy = ({ data, onSubmit, onSave, viewOnly = false, userEmail }) => {
    const [answers, setAnswers] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: 'info' });
    const [showCompletionDialog, setShowCompletionDialog] = useState(false);
    const [assessmentChanged, setAssessmentChanged] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    // Generate a unique storage key based on assessment ID and user email
    const getStorageKey = useCallback(() => {
        return `caseStudy_${data.id}_${userEmail || 'anonymous'}`;
    }, [data.id, userEmail]);

    // Save assessment version to localStorage
    const saveAssessmentVersion = useCallback(() => {
        const storageKey = `${getStorageKey()}_version`;
        // Create a version signature based on question IDs
        const versionSignature = data.questions.map(q => q.id).join('|');
        localStorage.setItem(storageKey, versionSignature);
        return versionSignature;
    }, [data.questions, getStorageKey]);

    // Check if assessment has changed
    const checkAssessmentChanged = useCallback(() => {
        const storageKey = `${getStorageKey()}_version`;
        const savedVersion = localStorage.getItem(storageKey);
        if (!savedVersion) return false;

        // Create current version signature
        const currentVersion = data.questions.map(q => q.id).join('|');
        return savedVersion !== currentVersion;
    }, [data.questions, getStorageKey]);

    // Merge saved answers with current questions
    const mergeAnswersWithCurrentQuestions = useCallback((savedAnswers) => {
        // Create a map of saved answers by question ID for easy lookup
        const savedAnswersMap = savedAnswers.reduce((map, answer) => {
            map[answer.id] = {
                user_response: answer.user_response || '',
                user_video: answer.user_video || null
            };
            return map;
        }, {});

        // Create new answers array based on current questions
        const mergedAnswers = data.questions.map(question => {
            // If we have a saved answer for this question, use it
            const savedData = savedAnswersMap[question.id] || { user_response: null, user_video: null };

            return {
                ...question,
                user_response: savedData.user_response,
                user_video: savedData.user_video
            };
        });

        return mergedAnswers;
    }, [data.questions]);

    // Load answers from localStorage on initial render
    useEffect(() => {
        const loadFromLocalStorage = () => {
            const storageKey = getStorageKey();
            if (!storageKey) return false;

            const savedData = localStorage.getItem(storageKey);
            if (!savedData) return false;

            try {
                const parsedData = JSON.parse(savedData);
                if (Array.isArray(parsedData) && parsedData.length > 0) {
                    // Check if assessment has changed
                    const hasChanged = checkAssessmentChanged();
                    setAssessmentChanged(hasChanged);

                    // Merge saved answers with current questions
                    const mergedAnswers = mergeAnswersWithCurrentQuestions(parsedData);
                    setAnswers(mergedAnswers);

                    // Save new assessment version
                    saveAssessmentVersion();

                    // Show appropriate message
                    setSaveStatus({
                        show: true,
                        message: hasChanged ? 'Assessment has been updated. Your previous answers have been preserved.' : 'Loaded previously saved answers',
                        type: hasChanged ? 'warning' : 'info'
                    });
                    return true;
                }
            } catch (e) {
                console.error('Error parsing stored answers:', e);
            }
            return false;
        };

        const loaded = loadFromLocalStorage();
        if (!loaded) {
            if (data?.responses && typeof data.responses === 'object') {
                // Merge server responses with current questions
                const mergedAnswers = mergeAnswersWithCurrentQuestions(data.responses);
                setAnswers(mergedAnswers);
            } else if (data.responses && typeof data.responses === 'string') {
                try {
                    const parsedResponses = JSON.parse(data.responses);
                    if (typeof parsedResponses === 'object') {
                        // Merge server responses with current questions
                        const mergedAnswers = mergeAnswersWithCurrentQuestions(parsedResponses);
                        setAnswers(mergedAnswers);
                    }
                } catch (e) {
                    console.error('Error parsing JSON responses:', e);

                    // Initialize answers with question IDs
                    const initialAnswers = data.questions.map(q => ({
                        ...q,
                        user_response: null,
                        user_video: null
                    }));
                    setAnswers(initialAnswers);
                }
            } else {
                // Initialize answers with question IDs
                const initialAnswers = data.questions.map(q => ({
                    ...q,
                    user_response: null,
                    user_video: null
                }));
                setAnswers(initialAnswers);
            }

            // Save assessment version
            saveAssessmentVersion();
        }
    }, [data, userEmail, getStorageKey, checkAssessmentChanged, mergeAnswersWithCurrentQuestions, saveAssessmentVersion]);

    // // Save answers to localStorage with immediate feedback
    // const saveToLocalStorage = useCallback(() => {
    //     if (viewOnly || !answers || answers.length === 0) return;

    //     const storageKey = getStorageKey();
    //     localStorage.setItem(storageKey, JSON.stringify(answers));

    //     // Show "Saved locally" message briefly
    //     setSaveStatus({ type: 'saving', message: 'Saving locally...' });

    //     // Clear the message after a delay
    //     setTimeout(() => {
    //         setSaveStatus(prev => prev.type === 'saving' ? { type: 'saved', message: 'Saved' } : prev);
    //     }, 1500);
    // }, [answers, viewOnly, getStorageKey]);

    useEffect(() => {
        // Skip initial render or when answers is empty
        if (answers && answers.length > 0) {
            const storageKey = getStorageKey();
            localStorage.setItem(storageKey, JSON.stringify(answers));
        }
    }, [answers, getStorageKey]);

    // Save to API with feedback
    const saveToServer = async () => {
        if (viewOnly) return;

        setSaveStatus({ type: 'saving', message: 'Saving...' });

        try {
            await axios.patch(`/api/assessments/${data.id}`, {
                responses: answers,
                email: userEmail,
                status: 'started'
            });

            setSaveStatus({ type: 'saving', message: 'Saved to server' });

            // Clear the success message after a delay
            setTimeout(() => {
                setSaveStatus(prev => prev.type === 'saving' ? { type: 'saved', message: 'Saved' } : prev);
            }, 1500);

        } catch (error) {
            console.error("Error saving to server:", error);
            setSaveStatus({ type: 'error', message: 'Server save failed' });
        }
    };


    // Handle text answer changes - using question ID to find and update the correct answer
    const handleAnswerChange = (value) => {
        if (viewOnly) return;

        const currentQuestion = data.questions[currentQuestionIndex];
        const currentQuestionId = currentQuestion.id;

        setAnswers(prev =>
            prev.map(answer =>
                (answer.id === currentQuestionId)
                    ? { ...answer, user_response: value }
                    : answer
            )
        );

        // Save to localStorage on each change
        // saveToLocalStorage();
    };

    // Handle video change - using question ID to update the video URL separately
    const handleVideoChange = (videoUrl, fileName = "Unknown") => {

        if (viewOnly) return;

        const currentQuestion = data.questions[currentQuestionIndex];
        const currentQuestionId = currentQuestion.id;

        // If videoUrl is empty, remove the video
        if (!videoUrl) {
            setAnswers(prev =>
                prev.map(answer =>
                    (answer.id === currentQuestionId)
                        ? { ...answer, user_video: null }
                        : answer
                )
            );
            return;
        }

        const baseVideoUrl = videoUrl ? videoUrl.split('?')[0] : '';

        const videoObject = {
            url: baseVideoUrl,
            fileName: fileName,
            uploadTime: new Date().toISOString()
        };

        setAnswers(prev =>
            prev.map(answer =>
                (answer.id === currentQuestionId)
                    ? { ...answer, user_video: videoObject }
                    : answer
            )
        );
    };

    // Handle video file upload
    const handleVideoUpload = async (event) => {
        if (viewOnly) return;

        const file = event.target.files[0];
        if (!file) return;

        // Check if file is a video
        if (!file.type.startsWith('video/')) {
            setSaveStatus({ show: true, message: 'Please upload a video file', type: 'error' });
            return;
        }

        // Check file size (limit to 700MB)
        const maxSize = 700 * 1024 * 1024; // 700MB
        if (file.size > maxSize) {
            setSaveStatus({ show: true, message: 'Video file is too large (max 700MB)', type: 'error' });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Create form data for upload
            const formData = new FormData();
            formData.append('files', file);
            formData.append('id', data.questions[currentQuestionIndex].id);
            formData.append('email', userEmail);

            // Upload the video with progress tracking
            const response = await axios.post(`/api/assessments/${data.id}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            // Update answer with video URL from response - using the dedicated video field
            if (response.data && response.data.data) {

                handleVideoChange(response.data.data, file.name);

                // Show success message
                setSaveStatus({ show: true, message: 'Video uploaded successfully', type: 'success' });
            }
        } catch (error) {
            console.error('Error uploading video:', error);
            setSaveStatus({ show: true, message: 'Failed to upload video', type: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    // Check if a question is answered (text or video)
    const isQuestionAnswered = (answer) => {
        if (!answer) return false;

        const isVideoRequired = answer.is_video_required;

        if (isVideoRequired) {
            return answer.user_video && answer.user_video.url && answer.user_video.url.trim() !== '';
        } else {
            return answer.user_response && answer.user_response.trim() !== '';
        }
    };

    // Get submission requirements - updated to check both text responses and video URLs
    const getSubmissionRequirements = () => {
        // Find answered questions by checking appropriate fields based on question type
        const answeredQuestions = answers.filter(answer => isQuestionAnswered(answer));
        const answeredCount = answeredQuestions.length;

        // Find required questions
        const requiredQuestions = data.questions.filter(q => q.is_required);
        const requiredCount = requiredQuestions.length;
        const minRequired = data.minimum_to_complete || 1;

        // Check if all required questions are answered by matching IDs
        const allRequiredAnswered = requiredQuestions.every((req) => {
            const correspondingAnswer = answers.find(a => (a.id === req.id));
            return isQuestionAnswered(correspondingAnswer);
        });

        // Count answered required questions
        const answeredRequiredCount = requiredQuestions.filter((req) => {
            const correspondingAnswer = answers.find(a => (a.id === req.id));
            return isQuestionAnswered(correspondingAnswer);
        }).length;

        const canSubmit = answeredCount >= minRequired && allRequiredAnswered;

        return {
            answeredCount,
            requiredCount,
            minRequired,
            allRequiredAnswered,
            answeredRequiredCount,
            canSubmit
        };
    };

    const handleSubmit = async () => {
        const requirements = getSubmissionRequirements();

        if (!requirements.canSubmit) {
            setShowCompletionDialog(true);
            return;
        }

        // First save to server
        await saveToServer();

        // Then call the onSubmit prop
        onSubmit(answers);

        // After successful submission, clear localStorage
        const storageKey = getStorageKey();
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}_version`);
    };

    // Navigate to next question AND save to server
    const goToNextQuestion = async () => {
        if (currentQuestionIndex < data?.questions?.length - 1) {
            // Save to server before navigating
            await saveToServer();

            // Then navigate
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    // Navigate to previous question AND save to server
    const goToPrevQuestion = async () => {
        if (currentQuestionIndex > 0) {
            // Save to server before navigating
            await saveToServer();

            // Then navigate
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    // Jump to a specific question
    const goToQuestion = async (index) => {
        // Save current question before navigating
        await saveToServer();
        setCurrentQuestionIndex(index);
    };

    const getSaveStatusDisplay = () => {
        switch (saveStatus.type) {
            case 'saving':
                return { text: 'Saving...', color: 'darkgrey', style: 'italic' };
            case 'error':
                return { text: 'Save failed', color: 'red', style: 'normal' };
            case 'warning':
                return { text: 'Assessment Changed', color: 'orange', style: 'normal' };
            case 'saved': // Default state when idle
            default:
                return { text: 'Saved', color: 'green', style: 'normal' };
        }
    };

    // Get the current answer value for display - using question ID
    const getCurrentAnswerValue = () => {
        if (!data?.questions?.[currentQuestionIndex]) return '';

        const currentQuestion = data.questions[currentQuestionIndex];
        const currentQuestionId = currentQuestion.id;

        const answer = answers.find(a => (a.id === currentQuestionId));

        return answer?.user_response || '';
    };

    // Get the current video URL for display
    const getCurrentVideo = () => {
        if (!data?.questions?.[currentQuestionIndex]) return null;

        const currentQuestion = data.questions[currentQuestionIndex];
        const currentQuestionId = currentQuestion.id;

        const answer = answers.find(a => (a.id === currentQuestionId));

        return answer?.user_video || null;
    };

    // Then in your JSX for the save status indicator
    const statusDisplay = getSaveStatusDisplay();

    const handleCloseSnackbar = () => {
        setSaveStatus(prev => ({ ...prev, show: false }));
    };

    // Check if current question requires video
    const isVideoRequired = data?.questions?.[currentQuestionIndex]?.is_video_required;

    // Assessment changed notification dialog
    const AssessmentChangedDialog = () => (
        <Dialog
            open={assessmentChanged}
            onClose={() => setAssessmentChanged(false)}
        >
            <DialogTitle sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                <Warning sx={{ mr: 1 }} /> Assessment Has Been Updated
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This assessment has been modified since you last worked on it. Your previous answers have been preserved, but there may be new questions that require your attention.
                </DialogContentText>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" fontWeight="600">
                        What this means:
                    </Typography>
                    <ul>
                        <li>Your existing answers are still available</li>
                        <li>New questions may have been added</li>
                        <li>Some questions might have been changed or removed</li>
                        <li>Please review all questions before submitting</li>
                    </ul>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setAssessmentChanged(false)} color="primary" variant="contained">
                    I Understand
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Video Upload Component
    const VideoUploadBox = () => {
        const currentVideo = getCurrentVideo();
        const hasVideo = currentVideo && currentVideo.url && currentVideo.url.trim() !== '';

        return (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#424242', display: 'flex', alignItems: 'center' }}>
                    <VideoCall sx={{ mr: 1 }} /> Your Video Response
                    {data.questions[currentQuestionIndex]?.is_required && (
                        <Typography component="span" color="error" sx={{ ml: 1 }}>*</Typography>
                    )}
                </Typography>

                {hasVideo ? (
                    // Display video info with success message
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Box sx={{
                            p: 4,
                            backgroundColor: '#f0f7f0', // Light green background
                            borderRadius: 1,
                            border: '1px solid #d0e0d0',
                            textAlign: 'center'
                        }}>
                            <Typography variant="body1" sx={{ color: '#2e7d32' }}>
                                <CheckCircle color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Video successfully uploaded
                            </Typography>

                            {/* Display additional video information */}
                            <Box sx={{ mt: 2, textAlign: 'left', pl: 2 }}>
                                <Typography variant="body2" sx={{ color: '#555', display: 'flex', gap: 1 }}>
                                    <strong>File name:</strong> {currentVideo.fileName}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#555', display: 'flex', gap: 1 }}>
                                    <strong>Uploaded on:</strong> {new Date(currentVideo.uploadTime).toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>

                        {!viewOnly && (
                            <Button
                                variant="outlined"
                                color="primary"
                                sx={{ mt: 2 }}
                                onClick={() => handleVideoChange('')}
                            >
                                Replace Video
                            </Button>
                        )}
                    </Box>
                ) : (
                    // Show upload interface
                    <Box sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: viewOnly ? '#f5f5f5' : 'white',
                    }}>
                        {isUploading ? (
                            // Show upload progress
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Uploading: {uploadProgress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{ height: 10, borderRadius: 5, mb: 2 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Please wait while your video uploads...
                                </Typography>
                            </Box>
                        ) : (
                            // Show upload button
                            <>
                                <input
                                    accept="video/*"
                                    id="video-upload-button"
                                    type="file"
                                    onChange={handleVideoUpload}
                                    disabled={viewOnly}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="video-upload-button">
                                    <Button
                                        variant="contained"
                                        component="span"
                                        startIcon={<CloudUpload />}
                                        disabled={viewOnly}
                                        sx={{ mb: 2 }}
                                    >
                                        Upload Video
                                    </Button>
                                </label>
                                <Typography variant="body2" color="text.secondary">
                                    Upload a video response (Max 700MB)
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Supported formats: MP4, MOV, AVI, WEBM
                                </Typography>
                                {data.questions[currentQuestionIndex]?.is_required && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                        This question requires a video response
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                )}

                {/* Save status indicator */}
                {!viewOnly && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: '0.5em',
                            mt: 1
                        }}
                    >
                        <span style={{ display: 'flex', gap: '0.5em', color: statusDisplay.color, fontStyle: statusDisplay.style }}>
                            {saveStatus.type === 'saving' ? <Edit fontSize='small' /> : saveStatus.type === 'error' ? <Error fontSize='small' /> : <TaskAlt fontSize='small' />} {statusDisplay.text}
                        </span>
                        <span>
                            <Info fontSize='small' sx={{ mr: '0.25em' }} /> Responses are auto saved
                        </span>
                    </Typography>
                )}
            </Box>
        );
    };

    // Calculate completion statistics
    const requirements = getSubmissionRequirements();
    const completionPercentage = (requirements.answeredCount / (requirements.minRequired || 1)) * 100;
    const displayPercentage = Math.min(completionPercentage, 100);

    return (
        <>
            <div className={Styles.caseStudyPage}>
                {/* Assessment Changed Dialog */}
                <AssessmentChangedDialog />

                {viewOnly && (
                    <Box
                        sx={{
                            backgroundColor: '#fff3e0',
                            p: 2,
                            mb: 2,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography variant="subtitle1" color="warning.main">
                            Preview Mode - Responses Cannot Be Submitted
                        </Typography>
                    </Box>
                )}

                {(!!data.is_test_mode) && (
                    <Box
                        sx={{
                            backgroundColor: '#fff3e0',
                            p: 2,
                            mb: 2,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography variant="subtitle1" color="warning.main">
                            You are currently in test mode. Responses submitted here will not affect the live assessment stats.
                        </Typography>
                    </Box>
                )}

                {/* Header Card */}
                <Card className={Styles.headerCard}>
                    <CardContent className={Styles.headerContent}>
                        <Typography variant="h4" className={Styles.studyName}>
                            {data?.name}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" className={Styles.studyDescription}>
                            {data?.description}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Progress Indicator */}
                <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 2 }}>
                                Question {currentQuestionIndex + 1} of {data.questions?.length}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                            Progress: {Math.round(displayPercentage)}% (Answer any {requirements.minRequired} of {data.questions.length})
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
                        {/* Linear Progress bar */}
                        <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={displayPercentage}
                                sx={{ height: 10, borderRadius: 5 }}
                            />
                        </Box>
                        {/* Stepper Container */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Stepper
                                activeStep={currentQuestionIndex}
                                alternativeLabel
                                connector={<StepConnector />}
                                sx={{
                                    maxWidth: '100%',
                                    overflow: 'auto',
                                    minWidth: '10%'
                                }}
                            >
                                {data.questions?.map((question, index) => {
                                    const isRequired = question?.is_required;
                                    const answer = answers.find(a => (a.id === question.id));

                                    const isAnswered = isQuestionAnswered(answer)

                                    return (
                                        <Step
                                            key={question.id}
                                            completed={isAnswered}
                                            onClick={() => goToQuestion(index)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <StepLabel
                                                optional={isRequired ?
                                                    <Typography variant="caption" color="error">* Required</Typography> : null
                                                }
                                            >
                                                <Tooltip title={question.name}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: isAnswered ? 'success.main' : 'inherit',
                                                            fontWeight: currentQuestionIndex === index ? 'bold' : 'normal'
                                                        }}
                                                    >
                                                        {question.name}
                                                    </Typography>
                                                </Tooltip>
                                            </StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                        </Box>
                    </Box>
                </Paper>

                {/* Navigation Card with Scenario, Question and Answer */}
                <Card className={Styles.navigationCard} sx={{ mb: 4 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, alignItems: 'center' }}>
                            <Typography variant="h5" align="center" sx={{ color: '#1450a0' }}>
                                {data.questions?.[currentQuestionIndex]?.name}
                            </Typography>
                            {data.questions[currentQuestionIndex]?.is_required && (
                                <Chip
                                    label="Required"
                                    size="small"
                                    color="error"
                                    icon={<Warning fontSize="small" />}
                                    sx={{ ml: 2 }}
                                />
                            )}
                            {data.questions[currentQuestionIndex]?.is_video_required && (
                                <Chip
                                    label="Video Response"
                                    size="small"
                                    color="secondary"
                                    icon={<VideoCall fontSize="small" />}
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Scenario Section */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#e7edf5', borderRadius: 2, borderLeft: '4px solid #1450a0' }}>
                            <Typography variant="h6" sx={{ mb: 1, color: '#424242' }}>
                                Scenario
                            </Typography>
                            <Typography variant="body1">
                                {data.questions?.[currentQuestionIndex]?.scenario}
                            </Typography>
                        </Box>

                        {/* Question Section */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f1f1f1', borderRadius: 2, borderLeft: '4px solid #6a6a6a' }}>
                            <Typography variant="h6" sx={{ mb: 1, color: '#424242', display: 'flex', alignItems: 'center' }}>
                                Question
                                {data.questions[currentQuestionIndex]?.is_required && (
                                    <Typography component="span" color="error" sx={{ ml: 1 }}>*</Typography>
                                )}
                            </Typography>
                            <Typography variant="body1">
                                {data.questions?.[currentQuestionIndex]?.question}
                            </Typography>
                        </Box>

                        {/* Conditional rendering based on whether video is required */}
                        {isVideoRequired ? (
                            <VideoUploadBox />
                        ) : (
                            /* Text Answer Section - updated to use getCurrentAnswerValue */
                            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 2 }}>
                                <Typography variant="h6" sx={{ mb: 1, color: '#424242' }}>
                                    Your Answer
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    variant="outlined"
                                    placeholder={`Type your answer here${data.questions[currentQuestionIndex]?.is_required ? ' (required)' : ''}...`}
                                    value={getCurrentAnswerValue()}
                                    onChange={(e) => handleAnswerChange(e.target.value)}
                                    disabled={viewOnly}
                                    error={data.questions[currentQuestionIndex]?.is_required && (!getCurrentAnswerValue() || getCurrentAnswerValue().trim() === '')}
                                    helperText={data.questions[currentQuestionIndex]?.is_required && (!getCurrentAnswerValue() || getCurrentAnswerValue().trim() === '') ? "This question requires an answer" : ""}
                                    sx={{
                                        backgroundColor: viewOnly ? '#f5f5f5' : 'white',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1,
                                            ...(data.questions[currentQuestionIndex]?.is_required && {
                                                borderColor: 'error.main'
                                            })
                                        }
                                    }}
                                    className={Styles.answerField}
                                />

                                {/* Save status indicator */}
                                {!viewOnly && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            px: '0.5em',
                                        }}
                                    >
                                        <span style={{ display: 'flex', gap: '0.5em', color: statusDisplay.color, fontStyle: statusDisplay.style }}>
                                            {saveStatus.type === 'saving' ? <Edit fontSize='small' /> : <TaskAlt fontSize='small' />} {statusDisplay.text}
                                        </span>
                                        <span>
                                            <Info fontSize='small' sx={{ mr: '0.25em' }} /> Responses are auto saved
                                        </span>
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Divider sx={{ mb: 3 }} />

                        {/* Navigation Controls */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={goToPrevQuestion}
                                disabled={currentQuestionIndex === 0}
                                startIcon={<ArrowBack />}
                            >
                                Previous Question
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={goToNextQuestion}
                                disabled={currentQuestionIndex === data.questions?.length - 1}
                                endIcon={<ArrowForward />}
                            >
                                Next Question
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Submit Button with Completion Status */}
                <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    {!viewOnly && (
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa', width: '100%', mb: 2, border: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Submission Status:
                            </Typography>
                            <Box sx={{ pl: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: requirements.answeredCount >= requirements.minRequired ? 'success.main' : 'text.secondary'
                                    }}
                                >
                                    {requirements.answeredCount >= requirements.minRequired ?
                                        <CheckCircle fontSize="small" sx={{ mr: 1 }} /> :
                                        <Info fontSize="small" sx={{ mr: 1 }} />}
                                    Minimum {requirements.minRequired} case studies: {requirements.answeredCount}/{requirements.minRequired} completed
                                </Typography>

                                {requirements.requiredCount > 0 && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: requirements.allRequiredAnswered ? 'success.main' : 'error.main'
                                        }}
                                    >
                                        {requirements.allRequiredAnswered ?
                                            <CheckCircle fontSize="small" sx={{ mr: 1 }} /> :
                                            <Error fontSize="small" sx={{ mr: 1 }} />}
                                        Required case studies: {requirements.answeredRequiredCount}/{requirements.requiredCount} completed
                                    </Typography>
                                )}

                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        fontWeight: 'bold',
                                        color: requirements.canSubmit ? 'success.main' : 'text.secondary'
                                    }}
                                >
                                    {requirements.canSubmit ? (
                                        <>
                                            <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Ready to submit
                                        </>
                                    ) : (
                                        "You need to complete the required questions before submitting"
                                    )}
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        endIcon={<Send />}
                        disabled={viewOnly || !requirements.canSubmit}
                        sx={{
                            mb: 3,
                            fontSize: '1.1rem',
                            py: 1.5,
                            px: 4
                        }}
                    >
                        Submit All
                    </Button>
                </Box>
            </div>

            {/* Incomplete Submission Dialog */}
            <Dialog
                open={showCompletionDialog}
                onClose={() => setShowCompletionDialog(false)}
            >
                <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
                    <Warning sx={{ mr: 1 }} /> Cannot Submit Yet
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You cannot submit this assessment yet because:
                    </DialogContentText>
                    <Box sx={{ pl: 2, mt: 2 }}>
                        {requirements.answeredCount < requirements.minRequired && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Error fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                                You need to complete at least {requirements.minRequired} case studies.
                                (Current: {requirements.answeredCount}/{requirements.minRequired})
                            </Typography>
                        )}

                        {!requirements.allRequiredAnswered && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Error fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                                You must complete all required case studies marked with <span style={{ color: 'red', marginLeft: '4px', marginRight: '4px' }}>*</span>
                                (Current: {requirements.answeredRequiredCount}/{requirements.requiredCount})
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCompletionDialog(false)} color="primary">
                        Continue Working
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for save status notifications */}
            <Snackbar
                open={saveStatus.show}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={saveStatus.type} sx={{ width: '100%' }}>
                    {saveStatus.message}
                </Alert>
            </Snackbar>
        </>
    );
};